"use client"

/**
 * The staff SSO bridge (`@ahead-health/core/ui/staffSso`, mounted by
 * `StaffEditorGate`) persists the signed-in user's Firebase idToken first-party
 * under this key. Re-reading it lets a standalone internal page reuse the
 * bridge's existing session without re-mounting it.
 *
 * This is UI-gating ONLY — every privileged route re-verifies the token
 * server-side via `authorizeSystemAccess`, so a missing/forged/expired value
 * just means "show the sign-in prompt", never an authorization decision.
 */
const TOOLBAR_TOKEN_KEY = "ahead-feedback-toolbar-token"

export interface ToolbarToken {
  idToken: string
  expiresAt: number
}

export function readToolbarToken(): ToolbarToken | null {
  if (typeof window === "undefined") return null
  const raw = window.localStorage.getItem(TOOLBAR_TOKEN_KEY)
  if (!raw) return null
  // The value is whatever sits in a user's localStorage — a corrupt/non-JSON
  // string is expected input here, not a logic bug, so a parse failure resolves
  // to "no token" (mirroring ahead-core's own loadStoredToken).
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (
    parsed !== null &&
    typeof parsed === "object" &&
    typeof (parsed as ToolbarToken).idToken === "string" &&
    typeof (parsed as ToolbarToken).expiresAt === "number"
  ) {
    const t = parsed as ToolbarToken
    return { idToken: t.idToken, expiresAt: t.expiresAt }
  }
  return null
}
